import { useEffect, useState, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import AppLayout from '../../layouts/AppLayout.jsx';
import ApiClient from '../../utils/ApiClient.js';
import envConfig from '../../utils/EnvironmentConfigs.js';
import { parseRequestId, formatRequestId } from '../../utils/RequestIdHelper.js';
import CopyableEmail from '../../components/CopyableEmail';
import ValueWithSkeleton from '../../components/ValueWithSkeleton/ValueWithSkeleton';
import {
    firstResolvedDisplayName,
    sanitizeDisplayName,
    normalizeDisplayValue,
} from '../../utils/DisplayValueUtils.js';
import { getFirstAttributeValue } from '../../utils/KeycloakAttributeUtils.js';
import { getUserTypeDisplayLabel } from '../../utils/UserTypeUtils.js';
import { formatStatusForDisplay } from '../../utils/StatusDisplayHelper.js';
import renderMarkdownToSafeHtml from '../../utils/MarkdownRenderer.js';

const normalizePotentialApprovers = (users) => {
    const members = Array.isArray(users) ? users : [];
    const seen = new Set();
    return members
        .map((member, index) => {
            const fallbackId = `member-${index}`;
            if (typeof member === 'string' || typeof member === 'number') {
                const id = String(member).trim();
                return {
                    id: id || fallbackId,
                    userLookupId: id,
                    displayName: sanitizeDisplayName(id, 'Not provided'),
                    email: '',
                };
            }
            if (!member || typeof member !== 'object') {
                return null;
            }
            const id = String(
                member.id ??
                member.uuid ??
                member.userId ??
                member.memberId ??
                member.subject ??
                ''
            ).trim();
            const displayName = firstResolvedDisplayName(
                [
                    member.name,
                    `${member.firstName || member.given_name || ''} ${member.lastName || member.family_name || ''}`.trim(),
                    member.displayName,
                    member.preferred_username,
                    member.username,
                    member.email,
                    id,
                ],
                'Not provided'
            );
            const email = String(
                member.email ??
                member.mail ??
                member.userPrincipalName ??
                ''
            ).trim();
            return {
                id: id || fallbackId,
                userLookupId: id,
                displayName,
                email,
            };
        })
        .filter(Boolean)
        .filter((member) => {
            const dedupeKey = `${member.id}|${member.displayName}|${member.email}`;
            if (seen.has(dedupeKey)) {
                return false;
            }
            seen.add(dedupeKey);
            return true;
        });
};

const extractGroupMembers = (value) => {
    if (!value) {
        return [];
    }
    if (Array.isArray(value)) {
        return value;
    }
    if (Array.isArray(value.content)) {
        return value.content;
    }
    if (Array.isArray(value.users)) {
        return value.users;
    }
    if (Array.isArray(value.members)) {
        return value.members;
    }
    return [];
};

const resolveRequesterOptionalField = (values) => {
    const candidates = Array.isArray(values) ? values : [values];
    for (const value of candidates) {
        const normalizedValue = normalizeDisplayValue(value);
        if (normalizedValue) {
            return normalizedValue;
        }
    }
    return '';
};
const resolveRequesterProfileField = (requesterUser, attributeKeys, fallbackValues = []) => {
    const attributeValue = getFirstAttributeValue(requesterUser?.attributes, attributeKeys, '');
    const fallbackList = Array.isArray(fallbackValues) ? fallbackValues : [fallbackValues];
    return resolveRequesterOptionalField([attributeValue, ...fallbackList]);
};

const RequestDetailPage = ({ authDetails, onSignOut }) => {
    const { requestId: routeRequestId } = useParams();
    const [requestData, setRequestData] = useState(null);
    const [requestError, setRequestError] = useState('');
    const [isLoadingRequest, setIsLoadingRequest] = useState(true);
    const [reviewData, setReviewData] = useState([]);
    const [supervisorDetails, setSupervisorDetails] = useState(null);
    const [supervisorPhotoUrl, setSupervisorPhotoUrl] = useState(null);
    const [isLoadingSupervisor, setIsLoadingSupervisor] = useState(false);
    const [requesterDetails, setRequesterDetails] = useState(null);
    const [requesterPhotoUrl, setRequesterPhotoUrl] = useState(null);
    const [requestedGroup, setRequestedGroup] = useState([]);
    const [entitlementOwnerGroup, setEntitlementOwnerGroup] = useState({});
    const [entitlementOwnerGroupPhotoUrl, setEntitlementOwnerGroupPhotoUrl] = useState(null);
    const [entitlementOwnerReviewerDetails, setEntitlementOwnerReviewerDetails] = useState(null);
    const [entitlementOwnerReviewerPhotoUrl, setEntitlementOwnerReviewerPhotoUrl] = useState(null);
    const [isLoadingEntitlementOwnerReviewer, setIsLoadingEntitlementOwnerReviewer] = useState(false);
    const [entitlementOwnerPotentialApprovers, setEntitlementOwnerPotentialApprovers] = useState([]);
    const [isLoadingEntitlementOwnerPotentialApprovers, setIsLoadingEntitlementOwnerPotentialApprovers] = useState(false);
    const [entitlementOwnerPotentialApproverPhotoLookup, setEntitlementOwnerPotentialApproverPhotoLookup] = useState(new Map());
    const [entitlementAccessGuide, setEntitlementAccessGuide] = useState('');
    const [isLoadingEntitlementAccessGuide, setIsLoadingEntitlementAccessGuide] = useState(false);

    useEffect(() => {
        let isMounted = true;
        const apiUrl = envConfig.apiBaseUrl;

        if (!apiUrl) {
            setRequestError('API URL is not configured.');
            setIsLoadingRequest(false);
            return () => {
                isMounted = false;
            };
        }

        if (!routeRequestId) {
            setRequestError('Request ID is missing.');
            setIsLoadingRequest(false);
            return () => {
                isMounted = false;
            };
        }

        const resolvedRequestId = parseRequestId(routeRequestId);

        const fetchRequest = async () => {
            try {
                const response = await ApiClient.get(
                    `/access-requests/${encodeURIComponent(resolvedRequestId)}`
                );

                if (response.status !== 200) {
                    throw new Error(`API responded with ${response.status}`);
                }

                if (isMounted) {
                    setRequestData(response.data ?? null);
                }
            } catch (error) {
                if (isMounted) {
                    setRequestError('We could not load this access request.');
                }
            } finally {
                if (isMounted) {
                    setIsLoadingRequest(false);
                }
            }
        };

        fetchRequest();

        return () => {
            isMounted = false;
        };
    }, [routeRequestId]);

    useEffect(() => {
        let isMounted = true;
        const apiUrl = envConfig.apiBaseUrl;

        if (!apiUrl) {
            return () => {
                isMounted = false;
            };
        }

        if (!routeRequestId) {
            return () => {
                isMounted = false;
            };
        }

        const resolvedRequestId = parseRequestId(routeRequestId);

        const fetchReviews = async () => {
            try {
                const response = await ApiClient.get(
                    `/access-requests/${encodeURIComponent(resolvedRequestId)}/review`
                );

                if (response.status !== 200) {
                    throw new Error(`API responded with ${response.status}`);
                }

                if (isMounted) {
                    const reviews = Array.isArray(response.data) ? response.data : [];
                    setReviewData(reviews);
                }
            } catch (error) {
                if (isMounted) {
                    setReviewData([]);
                }
            }
        };

        fetchReviews();

        return () => {
            isMounted = false;
        };
    }, [routeRequestId]);

    const requester = requestData?.requester ?? requestData?.requestor ?? requestData?.user ?? {};
    const requesterLookupId = String(
        requestData?.requesterUuid ??
        requestData?.requesterId ??
        requestData?.requestorUuid ??
        requestData?.requestorId ??
        requester?.uuid ??
        requester?.id ??
        requester?.username ??
        requester?.preferred_username ??
        requester?.email ??
        ''
    ).trim();

    useEffect(() => {
        let isMounted = true;

        if (!requesterLookupId) {
            setRequesterDetails(null);
            return () => {
                isMounted = false;
            };
        }

        const fetchRequesterDetails = async () => {
            try {
                const response = await ApiClient.get(`/users/${encodeURIComponent(requesterLookupId)}`);

                if (response.status !== 200) {
                    throw new Error(`API responded with ${response.status}`);
                }

                if (isMounted) {
                    setRequesterDetails(response.data ?? null);
                }
            } catch (error) {
                if (isMounted) {
                    setRequesterDetails(null);
                }
            }
        };

        fetchRequesterDetails();

        return () => {
            isMounted = false;
        };
    }, [requesterLookupId]);

    useEffect(() => {
        let isMounted = true;
        let objectUrl = null;

        if (!requesterLookupId) {
            setRequesterPhotoUrl(null);
            return () => {
                isMounted = false;
            };
        }

        const fetchRequesterPhoto = async () => {
            try {
                const response = await ApiClient.get(`/users/${encodeURIComponent(requesterLookupId)}/picture`, {
                    responseType: 'blob',
                });

                if (response.status !== 200) {
                    throw new Error(`API responded with ${response.status}`);
                }

                const contentType = response.headers?.['content-type'] ?? '';
                if (contentType && !contentType.startsWith('image/')) {
                    throw new Error(`Unexpected content type: ${contentType}`);
                }

                objectUrl = URL.createObjectURL(response.data);

                if (isMounted) {
                    setRequesterPhotoUrl(objectUrl);
                }
            } catch (error) {
                if (isMounted) {
                    setRequesterPhotoUrl(null);
                }
            }
        };

        fetchRequesterPhoto();

        return () => {
            isMounted = false;
            if (objectUrl) {
                URL.revokeObjectURL(objectUrl);
            }
        };
    }, [requesterLookupId]);

    const request = requestData ?? {};
    const requestState = String(request.state ?? request.status ?? '').trim().toUpperCase();
    const isImplementedRequest = requestState === 'IMPLEMENTED';
    const statusLabel = formatStatusForDisplay(request.state ?? request.status, 'DRAFT');
    const approvalTypes = useMemo(() => {
        return new Set(
            reviewData
                .filter((review) => String(review?.decision ?? '').toUpperCase() === 'APPROVED')
                .map((review) => String(review?.type ?? '').toUpperCase())
        );
    }, [reviewData]);
    const currentStep = useMemo(() => {
        if (request.closed) {
            return 4;
        }
        if (approvalTypes.has('ENTITLEMENT_OWNER')) {
            return 4;
        }
        if (approvalTypes.has('SUPERVISOR')) {
            return 3;
        }
        return 2;
    }, [approvalTypes, request.closed]);
    const normalizeGroupId = (group) => {
        if (!group) {
            return '';
        }
        if (typeof group === 'string' || typeof group === 'number') {
            return String(group).trim();
        }
        if (group.id || group.uuid) {
            return String(group.id ?? group.uuid).trim();
        }
        return '';
    };
    const requestedGroupId =
        normalizeGroupId(request.groupUuid) ||
        normalizeGroupId(request.groupId) ||
        normalizeGroupId(request.group) ||
        '';
    const requestedRoles = Array.isArray(request.roles)
        ? request.roles
        : request.role
            ? [request.role]
            : request.roleName
                ? [request.roleName]
                : [];
    const normalizeRequestedEntitlementName = (entitlement) => {
        if (entitlement === null || entitlement === undefined) {
            return '';
        }
        if (typeof entitlement === 'string' || typeof entitlement === 'number') {
            return String(entitlement).trim();
        }
        return firstResolvedDisplayName(
            [entitlement.name, entitlement.displayName, entitlement.id, entitlement.uuid],
            ''
        );
    };
    const requestedRoleNames = requestedRoles
        .map((role) => normalizeRequestedEntitlementName(role))
        .filter(Boolean);
    const requestedEntitlementLabel = requestedRoleNames.length ? 'Requested role' : 'Requested group';
    const justification = request.justification ?? request.reason ?? '';
    const jobResponsibilities = request.jobResponsibilities ?? request.jobResponsibility ?? '';
    const embeddedRequester = request.requester ?? request.requestor ?? request.user ?? {};
    const supervisorUuid = request.supervisorUuid ?? request.supervisorId ?? '';
    const entitlementOwnerGroupId =
        normalizeGroupId(request.entitlementOwnerUuid) ||
        normalizeGroupId(request.entitlementOwnerId) ||
        normalizeGroupId(request.entitlementOwner) ||
        normalizeGroupId(request.entitlementOwnerGroupUuid) ||
        normalizeGroupId(request.entitlementOwnerGroupId) ||
        normalizeGroupId(Array.isArray(request.entitlementOwners) ? request.entitlementOwners[0] : null) ||
        '';
    const requesterFirstName =
        requesterDetails?.firstName ||
        requesterDetails?.given_name ||
        embeddedRequester?.firstName ||
        embeddedRequester?.given_name ||
        request.requesterFirstName ||
        authDetails?.identity?.tokenParsed?.given_name ||
        '';
    const requesterLastName =
        requesterDetails?.lastName ||
        requesterDetails?.family_name ||
        embeddedRequester?.lastName ||
        embeddedRequester?.family_name ||
        request.requesterLastName ||
        authDetails?.identity?.tokenParsed?.family_name ||
        '';
    const requesterUsername =
        requesterDetails?.username ||
        requesterDetails?.preferred_username ||
        embeddedRequester?.username ||
        embeddedRequester?.preferred_username ||
        request.requesterUsername ||
        authDetails?.identity?.tokenParsed?.preferred_username ||
        '';
    const requesterEmail =
        requesterDetails?.email ||
        embeddedRequester?.email ||
        request.requesterEmail ||
        authDetails?.identity?.tokenParsed?.email ||
        '';
    const requesterJobTitle = resolveRequesterProfileField(
        requesterDetails,
        ['jobTitle', 'JobTitle'],
        [
            requesterDetails?.jobTitle,
            requesterDetails?.job_title,
            embeddedRequester?.jobTitle,
            embeddedRequester?.job_title,
            request?.requesterJobTitle,
            request?.jobTitle,
        ]
    );
    const requesterUserType = getUserTypeDisplayLabel(
        resolveRequesterProfileField(
            requesterDetails,
            ['UserType', 'userType', 'user_type'],
            [
                requesterDetails?.userType,
                requesterDetails?.UserType,
                requesterDetails?.user_type,
                embeddedRequester?.userType,
                embeddedRequester?.UserType,
                embeddedRequester?.user_type,
                request?.requesterUserType,
                request?.requesterUsertype,
                request?.userType,
                request?.UserType,
            ]
        ),
        ''
    );
    const requesterContractNumber = resolveRequesterProfileField(
        requesterDetails,
        ['contractNumber', 'ContractNumber'],
        [
            requesterDetails?.contractNumber,
            requesterDetails?.contract_number,
            embeddedRequester?.contractNumber,
            embeddedRequester?.contract_number,
            request?.requesterContractNumber,
            request?.contractNumber,
        ]
    );
    const requesterContractExpirationDate = resolveRequesterProfileField(
        requesterDetails,
        ['contractExpirationDate', 'ContractExpirationDate'],
        [
            requesterDetails?.contractExpirationDate,
            requesterDetails?.contract_expiration_date,
            embeddedRequester?.contractExpirationDate,
            embeddedRequester?.contract_expiration_date,
            request?.requesterContractExpirationDate,
            request?.contractExpirationDate,
        ]
    );
    const supervisorDisplayName =
        firstResolvedDisplayName([
            supervisorDetails?.name,
            `${supervisorDetails?.firstName || ''} ${supervisorDetails?.lastName || ''}`.trim(),
            supervisorDetails?.preferred_username,
            supervisorDetails?.username,
        ], 'Supervisor');
    const supervisorEmail = supervisorDetails?.email ?? '';
    const currentUserEmail = authDetails?.identity?.tokenParsed?.email ?? '';
    useEffect(() => {
        let isMounted = true;

        const fetchEntitlementOwnerGroup = async () => {
            const groupId = String(entitlementOwnerGroupId || '').trim();
            if (!groupId) {
                if (isMounted) {
                    setEntitlementOwnerGroup({});
                }
                return;
            }

            try {
                const response = await ApiClient.get(`/groups/${encodeURIComponent(groupId)}`);
                const group = {
                    id: response.data.id ?? response.data.uuid ?? response.data.name ?? '',
                    name: firstResolvedDisplayName(
                        [response.data.name, response.data.displayName],
                        'Unknown group'
                    ),
                };

                if (isMounted) {
                    setEntitlementOwnerGroup(group);
                }
            } catch (error) {
                if (isMounted) {
                    setEntitlementOwnerGroup({});
                }
            }
        };

        fetchEntitlementOwnerGroup();

        return () => {
            isMounted = false;
        };
    }, [entitlementOwnerGroupId]);
    useEffect(() => {
        let isMounted = true;
        let objectUrl = null;
        const groupId = String(entitlementOwnerGroupId || '').trim();

        if (!groupId) {
            setEntitlementOwnerGroupPhotoUrl(null);
            return () => {
                isMounted = false;
            };
        }

        const fetchEntitlementOwnerGroupPhoto = async () => {
            try {
                const response = await ApiClient.get(`/groups/${encodeURIComponent(groupId)}/picture`, {
                    responseType: 'blob',
                });

                if (response.status !== 200) {
                    throw new Error(`API responded with ${response.status}`);
                }

                const contentType = response.headers?.['content-type'] ?? '';
                if (contentType && !contentType.startsWith('image/')) {
                    throw new Error(`Unexpected content type: ${contentType}`);
                }

                objectUrl = URL.createObjectURL(response.data);

                if (isMounted) {
                    setEntitlementOwnerGroupPhotoUrl(objectUrl);
                }
            } catch (error) {
                if (isMounted) {
                    setEntitlementOwnerGroupPhotoUrl(null);
                }
            }
        };

        fetchEntitlementOwnerGroupPhoto();

        return () => {
            isMounted = false;
            if (objectUrl) {
                URL.revokeObjectURL(objectUrl);
            }
        };
    }, [entitlementOwnerGroupId]);
    useEffect(() => {
        let isMounted = true;
        const groupId = String(requestedGroupId || '').trim();

        if (!groupId) {
            setRequestedGroup({});
            return () => {
                isMounted = false;
            };
        }

        const fetchRequestGroup = async () => {
            try {
                const response = await ApiClient.get(`/groups/${encodeURIComponent(groupId)}`);

                if (response.status !== 200) {
                    throw new Error(`API responded with ${response.status}`);
                }

                if (isMounted) {
                    const group = {
                        id: response.data.id ?? response.data.uuid ?? response.data.name ?? '',
                        name: firstResolvedDisplayName(
                            [response.data.name, response.data.displayName],
                            'Unknown group'
                        ),
                    }
                    setRequestedGroup(group);
                }
            } catch (error) {
                if (isMounted) {
                    setRequestedGroup({});
                }
            }
        };

        fetchRequestGroup();

        return () => {
            isMounted = false;
        };
    }, [requestedGroupId]);
    useEffect(() => {
        let isMounted = true;
        let objectUrl = null;
        const supervisorId = String(supervisorUuid || '').trim();

        if (!supervisorId) {
            setSupervisorDetails(null);
            setSupervisorPhotoUrl(null);
            return () => {
                isMounted = false;
            };
        }

        const fetchSupervisorDetails = async () => {
            setIsLoadingSupervisor(true);
            try {
                const response = await ApiClient.get(`/users/${encodeURIComponent(supervisorId)}`);

                if (response.status !== 200) {
                    throw new Error(`API responded with ${response.status}`);
                }

                if (isMounted) {
                    setSupervisorDetails(response.data);
                }
            } catch (error) {
                if (isMounted) {
                    setSupervisorDetails(null);
                }
            } finally {
                if (isMounted) {
                    setIsLoadingSupervisor(false);
                }
            }
        };

        const fetchSupervisorPhoto = async () => {
            try {
                const response = await ApiClient.get(`/users/${encodeURIComponent(supervisorId)}/picture`, {
                    responseType: 'blob',
                });

                if (response.status !== 200) {
                    throw new Error(`API responded with ${response.status}`);
                }

                const contentType = response.headers?.['content-type'] ?? '';
                if (contentType && !contentType.startsWith('image/')) {
                    throw new Error(`Unexpected content type: ${contentType}`);
                }

                objectUrl = URL.createObjectURL(response.data);

                if (isMounted) {
                    setSupervisorPhotoUrl(objectUrl);
                }
            } catch (error) {
                if (isMounted) {
                    setSupervisorPhotoUrl(null);
                }
            }
        };

        fetchSupervisorDetails();
        fetchSupervisorPhoto();

        return () => {
            isMounted = false;
            if (objectUrl) {
                URL.revokeObjectURL(objectUrl);
            }
        };
    }, [supervisorUuid]);
    const submittedAtRaw =
        request.createdAt ??
        request.submittedAt ??
        request.submittedDate ??
        request.createdDate ??
        request.requestedAt ??
        request.createdOn ??
        request.created ??
        request.requestDate ??
        request.dateSubmitted ??
        request.dateCreated ??
        request.submissionDate ??
        request.submissionTimestamp ??
        request.timestamp ??
        '';
    const normalizeSubmittedAt = (value) => {
        if (!value) {
            return null;
        }
        if (value instanceof Date) {
            return Number.isNaN(value.getTime()) ? null : value;
        }
        const rawValue = String(value).trim();
        if (!rawValue) {
            return null;
        }
        const hasTimezone = /[zZ]|[+-]\d{2}:?\d{2}$/.test(rawValue);
        const normalizedValue = hasTimezone ? rawValue : `${rawValue}Z`;
        const parsed = new Date(normalizedValue);
        if (!Number.isNaN(parsed.getTime())) {
            return parsed;
        }
        const fallback = new Date(rawValue);
        return Number.isNaN(fallback.getTime()) ? null : fallback;
    };
    const submittedAt = normalizeSubmittedAt(submittedAtRaw);
    const submittedAtDisplay = submittedAt
        ? submittedAt.toLocaleString()
        : 'Not provided';
    const updatedAtDisplay = request.updatedAt
        ? normalizeSubmittedAt(request.updatedAt)?.toLocaleString() ?? 'Not provided'
        : 'Not provided';
    const reviewsByType = reviewData.reduce((reviews, review) => {
        const reviewType = String(review?.type ?? '').toUpperCase();
        if (!reviewType) {
            return reviews;
        }

        const currentTimestamp = normalizeSubmittedAt(
            review?.updatedAt ?? review?.createdAt ?? ''
        );
        const existingReview = reviews[reviewType];
        const existingTimestamp = normalizeSubmittedAt(
            existingReview?.updatedAt ?? existingReview?.createdAt ?? ''
        );

        if (!existingReview || (currentTimestamp && (!existingTimestamp || currentTimestamp > existingTimestamp))) {
            reviews[reviewType] = review;
        }

        return reviews;
    }, {});
    const supervisorSummaryReview = reviewsByType.SUPERVISOR ?? null;
    const entitlementOwnerSummaryReview = reviewsByType.ENTITLEMENT_OWNER ?? null;
    const securityManagerSummaryReview = reviewsByType.SECURITY_MANAGER ?? null;
    const entitlementOwnerReviewerUuid =
        entitlementOwnerSummaryReview?.updatedBy ??
        entitlementOwnerSummaryReview?.createdBy ??
        '';
    const supervisorReviewDateRaw =
        supervisorSummaryReview?.updatedAt ??
        supervisorSummaryReview?.createdAt ??
        '';
    const supervisorReviewDateDisplay = supervisorReviewDateRaw
        ? normalizeSubmittedAt(supervisorReviewDateRaw)?.toLocaleString() ?? ''
        : '';
    const supervisorDecisionAtRaw =
        reviewsByType.SUPERVISOR?.updatedAt ??
        reviewsByType.SUPERVISOR?.createdAt ??
        '';
    const supervisorDecisionAt = normalizeSubmittedAt(supervisorDecisionAtRaw);
    const entitlementOwnerReviewDateRaw =
        entitlementOwnerSummaryReview?.updatedAt ??
        entitlementOwnerSummaryReview?.createdAt ??
        '';
    const entitlementOwnerReviewDateDisplay = entitlementOwnerReviewDateRaw
        ? normalizeSubmittedAt(entitlementOwnerReviewDateRaw)?.toLocaleString() ?? ''
        : '';
    const entitlementOwnerDecisionAtRaw =
        reviewsByType.ENTITLEMENT_OWNER?.updatedAt ??
        reviewsByType.ENTITLEMENT_OWNER?.createdAt ??
        '';
    const entitlementOwnerDecisionAt = normalizeSubmittedAt(entitlementOwnerDecisionAtRaw);
    const entitlementOwnerReviewerDisplayName =
        entitlementOwnerReviewerDetails?.name ||
        `${entitlementOwnerReviewerDetails?.firstName || ''} ${entitlementOwnerReviewerDetails?.lastName || ''}`.trim() ||
        entitlementOwnerReviewerDetails?.preferred_username ||
        entitlementOwnerReviewerDetails?.username ||
        'Not provided';
    const entitlementOwnerReviewerEmail = entitlementOwnerReviewerDetails?.email ?? '';
    useEffect(() => {
        let isMounted = true;
        const reviewerId = String(entitlementOwnerReviewerUuid || '').trim();

        if (!reviewerId) {
            setEntitlementOwnerReviewerDetails(null);
            setIsLoadingEntitlementOwnerReviewer(false);
            return () => {
                isMounted = false;
            };
        }

        const fetchEntitlementOwnerReviewerDetails = async () => {
            setIsLoadingEntitlementOwnerReviewer(true);
            try {
                const response = await ApiClient.get(`/users/${encodeURIComponent(reviewerId)}`);

                if (response.status !== 200) {
                    throw new Error(`API responded with ${response.status}`);
                }

                if (isMounted) {
                    setEntitlementOwnerReviewerDetails(response.data ?? null);
                }
            } catch (error) {
                if (isMounted) {
                    setEntitlementOwnerReviewerDetails(null);
                }
            } finally {
                if (isMounted) {
                    setIsLoadingEntitlementOwnerReviewer(false);
                }
            }
        };

        fetchEntitlementOwnerReviewerDetails();

        return () => {
            isMounted = false;
        };
    }, [entitlementOwnerReviewerUuid]);
    useEffect(() => {
        let isMounted = true;
        let objectUrl = null;
        const reviewerId = String(entitlementOwnerReviewerUuid || '').trim();

        if (!reviewerId) {
            setEntitlementOwnerReviewerPhotoUrl(null);
            return () => {
                isMounted = false;
            };
        }

        const fetchEntitlementOwnerReviewerPhoto = async () => {
            try {
                const response = await ApiClient.get(`/users/${encodeURIComponent(reviewerId)}/picture`, {
                    responseType: 'blob',
                });

                if (response.status !== 200) {
                    throw new Error(`API responded with ${response.status}`);
                }

                const contentType = response.headers?.['content-type'] ?? '';
                if (contentType && !contentType.startsWith('image/')) {
                    throw new Error(`Unexpected content type: ${contentType}`);
                }

                objectUrl = URL.createObjectURL(response.data);

                if (isMounted) {
                    setEntitlementOwnerReviewerPhotoUrl(objectUrl);
                }
            } catch (error) {
                if (isMounted) {
                    setEntitlementOwnerReviewerPhotoUrl(null);
                }
            }
        };

        fetchEntitlementOwnerReviewerPhoto();

        return () => {
            isMounted = false;
            if (objectUrl) {
                URL.revokeObjectURL(objectUrl);
            }
        };
    }, [entitlementOwnerReviewerUuid]);
    useEffect(() => {
        let isMounted = true;
        const groupId = String(entitlementOwnerGroupId || '').trim();

        if (!groupId || currentStep !== 3) {
            setEntitlementOwnerPotentialApprovers([]);
            setIsLoadingEntitlementOwnerPotentialApprovers(false);
            return () => {
                isMounted = false;
            };
        }

        const fetchEntitlementOwnerPotentialApprovers = async () => {
            setIsLoadingEntitlementOwnerPotentialApprovers(true);
            try {
                const response = await ApiClient.get(`/groups/${encodeURIComponent(groupId)}/members`);
                if (response.status !== 200) {
                    throw new Error(`API responded with ${response.status}`);
                }
                const members = extractGroupMembers(response.data);

                if (isMounted) {
                    setEntitlementOwnerPotentialApprovers(normalizePotentialApprovers(members));
                }
            } catch (error) {
                if (isMounted) {
                    setEntitlementOwnerPotentialApprovers([]);
                }
            } finally {
                if (isMounted) {
                    setIsLoadingEntitlementOwnerPotentialApprovers(false);
                }
            }
        };

        fetchEntitlementOwnerPotentialApprovers();

        return () => {
            isMounted = false;
        };
    }, [currentStep, entitlementOwnerGroupId]);
    useEffect(() => {
        let isMounted = true;
        let objectUrls = [];

        if (currentStep !== 3 || entitlementOwnerPotentialApprovers.length === 0) {
            setEntitlementOwnerPotentialApproverPhotoLookup(new Map());
            return () => {
                isMounted = false;
            };
        }

        const approversWithLookupIds = entitlementOwnerPotentialApprovers.filter((approver) =>
            String(approver?.userLookupId ?? '').trim()
        );
        if (approversWithLookupIds.length === 0) {
            setEntitlementOwnerPotentialApproverPhotoLookup(new Map());
            return () => {
                isMounted = false;
            };
        }

        const fetchPotentialApproverPhotos = async () => {
            try {
                const responses = await Promise.all(
                    approversWithLookupIds.map(async (approver) => {
                        const userLookupId = String(approver.userLookupId || '').trim();
                        if (!userLookupId) {
                            return null;
                        }
                        try {
                            const response = await ApiClient.get(`/users/${encodeURIComponent(userLookupId)}/picture`, {
                                responseType: 'blob',
                            });

                            if (response.status !== 200) {
                                throw new Error(`API responded with ${response.status}`);
                            }

                            const contentType = response.headers?.['content-type'] ?? '';
                            if (contentType && !contentType.startsWith('image/')) {
                                throw new Error(`Unexpected content type: ${contentType}`);
                            }

                            const objectUrl = URL.createObjectURL(response.data);
                            objectUrls = [...objectUrls, objectUrl];

                            return {
                                approverId: approver.id,
                                objectUrl,
                            };
                        } catch (error) {
                            return null;
                        }
                    })
                );

                const lookup = new Map();
                responses.filter(Boolean).forEach((item) => {
                    if (item?.approverId && item?.objectUrl) {
                        lookup.set(item.approverId, item.objectUrl);
                    }
                });

                if (isMounted) {
                    setEntitlementOwnerPotentialApproverPhotoLookup(lookup);
                }
            } catch (error) {
                if (isMounted) {
                    setEntitlementOwnerPotentialApproverPhotoLookup(new Map());
                }
            }
        };

        fetchPotentialApproverPhotos();

        return () => {
            isMounted = false;
            objectUrls.forEach((objectUrl) => {
                URL.revokeObjectURL(objectUrl);
            });
            objectUrls = [];
        };
    }, [currentStep, entitlementOwnerPotentialApprovers]);
    const calculateElapsedDays = (startDate, endDate) => {
        if (!startDate || !endDate) {
            return null;
        }
        const diffMs = endDate.getTime() - startDate.getTime();
        if (Number.isNaN(diffMs)) {
            return null;
        }
        const days = Math.ceil(diffMs / (1000 * 60 * 60 * 24));
        return Math.max(days, 0);
    };
    const supervisorDurationDays = useMemo(() => {
        if (!submittedAt) {
            return null;
        }
        if (currentStep >= 3) {
            return calculateElapsedDays(submittedAt, supervisorDecisionAt);
        }
        return calculateElapsedDays(submittedAt, new Date());
    }, [submittedAt, supervisorDecisionAt, currentStep]);
    const entitlementOwnerDurationDays = useMemo(() => {
        if (currentStep < 3) {
            return null;
        }
        if (currentStep >= 4) {
            return calculateElapsedDays(supervisorDecisionAt, entitlementOwnerDecisionAt);
        }
        return calculateElapsedDays(supervisorDecisionAt, new Date());
    }, [supervisorDecisionAt, entitlementOwnerDecisionAt, currentStep]);
    const reviewSections = [
        {
            title: 'Supervisor Review',
            review: supervisorSummaryReview,
        },
        {
            title: 'Entitlement Owner Review',
            review: entitlementOwnerSummaryReview,
        },
        {
            title: 'Security Manager Review',
            review: securityManagerSummaryReview,
        },
    ];
    const requestedGroupName = useMemo(() => {
        return (
            sanitizeDisplayName(requestedGroup.name) ||
            sanitizeDisplayName(typeof request.group === 'string' ? request.group : '') ||
            sanitizeDisplayName(request.groupName) ||
            'Not provided'
        );
    }, [requestedGroup, request.group, request.groupName]);
    const entitlementGuideLookupName = useMemo(() => {
        return (
            sanitizeDisplayName(requestedGroup.name, '') ||
            sanitizeDisplayName(
                typeof request.group === 'object' && request.group !== null
                    ? request.group.name ?? request.group.displayName
                    : '',
                ''
            ) ||
            sanitizeDisplayName(request.groupName, '')
        );
    }, [requestedGroup.name, request.group, request.groupName]);
    const entitlementOwnerGroupName = sanitizeDisplayName(entitlementOwnerGroup.name, 'Unknown group');
    useEffect(() => {
        let isMounted = true;

        if (!isImplementedRequest || !entitlementGuideLookupName) {
            setEntitlementAccessGuide('');
            setIsLoadingEntitlementAccessGuide(false);
            return () => {
                isMounted = false;
            };
        }

        const fetchEntitlementAccessGuide = async () => {
            setIsLoadingEntitlementAccessGuide(true);
            try {
                const response = await ApiClient.get(
                    `/groups/${encodeURIComponent(entitlementGuideLookupName)}/guide`
                );
                if (response.status !== 200) {
                    throw new Error(`API responded with ${response.status}`);
                }

                const guideText = String(response.data?.guide ?? '').trim();
                if (isMounted) {
                    setEntitlementAccessGuide(guideText);
                }
            } catch (error) {
                if (isMounted) {
                    setEntitlementAccessGuide('');
                }
            } finally {
                if (isMounted) {
                    setIsLoadingEntitlementAccessGuide(false);
                }
            }
        };

        fetchEntitlementAccessGuide();

        return () => {
            isMounted = false;
        };
    }, [isImplementedRequest, entitlementGuideLookupName]);

    const displayRequestId = formatRequestId(routeRequestId);
    const shouldShowEntitlementAccessGuide =
        isImplementedRequest && (isLoadingEntitlementAccessGuide || Boolean(entitlementAccessGuide));

    return (
        <AppLayout sidebar authDetails={authDetails} onSignOut={onSignOut}>
            <section className="usa-section">
                <div className="grid-container">
                    <div className="grid-row grid-gap">
                        <div className="tablet:grid-col-12">
                            <div className="usa-card">
                                <div className="usa-card__container">
                                    <div className="usa-card__header">
                                        <h2 className="usa-card__heading request-details__heading">
                                            Request Details - {displayRequestId}
                                        </h2>
                                    </div>
                                    <div className="usa-card__body">
                                        {isLoadingRequest ? (
                                            <div className="approval-details" aria-hidden="true">
                                                <span className="usa-checkbox__label-description">
                                                    <ValueWithSkeleton loading type="sentence" width="18ch" />
                                                </span>
                                                <div className="approval-summary-grid">
                                                    <div className="approval-summary-column">
                                                        <div className="usa-summary-box approval-summary-box">
                                                            <div className="usa-summary-box__body">
                                                                <h4 className="usa-summary-box__heading">
                                                                    <ValueWithSkeleton loading type="heading" width="14ch" />
                                                                </h4>
                                                                <div className="usa-summary-box__text">
                                                                    {Array.from({ length: 4 }).map((_, index) => (
                                                                        <p key={`request-detail-loading-left-${index}`}>
                                                                            <ValueWithSkeleton loading type="sentence" width="24ch" />
                                                                        </p>
                                                                    ))}
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                    <div className="approval-summary-column">
                                                        <div className="usa-summary-box approval-summary-box">
                                                            <div className="usa-summary-box__body">
                                                                <h4 className="usa-summary-box__heading">
                                                                    <ValueWithSkeleton loading type="heading" width="14ch" />
                                                                </h4>
                                                                <div className="usa-summary-box__text">
                                                                    {Array.from({ length: 4 }).map((_, index) => (
                                                                        <p key={`request-detail-loading-right-${index}`}>
                                                                            <ValueWithSkeleton loading type="sentence" width="20ch" />
                                                                        </p>
                                                                    ))}
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        ) : requestError ? (
                                            <div className="usa-alert usa-alert--error" role="alert">
                                                <div className="usa-alert__body">
                                                    <p className="usa-alert__text">{requestError}</p>
                                                </div>
                                            </div>
                                        ) : (
                                            <div className="approval-details">
                                                <span className="usa-checkbox__label-description">
                                                    <div
                                                        className="usa-step-indicator usa-step-indicator--counters approval-step-indicator"
                                                        aria-label="Request steps"
                                                    >
                                                        <ol className="usa-step-indicator__segments">
                                                            {[
                                                                'Submitted',
                                                                'Supervisor review',
                                                                'Entitlement owner review',
                                                                'Implemented / Closed',
                                                            ].map((label, index) => {
                                                                const stepNumber = index + 1;
                                                                const isComplete = stepNumber < currentStep;
                                                                const isCurrent = stepNumber === currentStep;
                                                                return (
                                                                    <li
                                                                        key={label}
                                                                        className={
                                                                            isComplete
                                                                                ? 'usa-step-indicator__segment usa-step-indicator__segment--complete'
                                                                                : isCurrent
                                                                                    ? 'usa-step-indicator__segment usa-step-indicator__segment--current'
                                                                                    : 'usa-step-indicator__segment'
                                                                        }
                                                                    >
                                                                        <span className="usa-step-indicator__segment-label">
                                                                            {label}
                                                                        </span>
                                                                    </li>
                                                                );
                                                            })}
                                                        </ol>
                                                    </div>
                                                </span>
                                                <div className="approval-summary-grid">
                                                    <div className="approval-summary-column">
                                                        <div
                                                            className="usa-summary-box approval-summary-box"
                                                            role="region"
                                                            aria-labelledby="request-submission-details"
                                                        >
                                                            <div className="usa-summary-box__body">
                                                                <h4
                                                                    className="usa-summary-box__heading"
                                                                    id="request-submission-details"
                                                                >
                                                                    Submission Details
                                                                </h4>
                                                                <div className="usa-summary-box__text">
                                                                    <p>
                                                                        <span className="approval-key">Status</span>
                                                                        {statusLabel}
                                                                    </p>
                                                                    <p>
                                                                        <span className="approval-key">Submitted</span>
                                                                        {submittedAtDisplay}
                                                                    </p>
                                                                    <p>
                                                                        <span className="approval-key">Last updated</span>
                                                                        {updatedAtDisplay}
                                                                    </p>
                                                                    <p>
                                                                        <span className="approval-key">Request type</span>
                                                                        {request.type ?? 'Not provided'}
                                                                    </p>
                                                                    <p>
                                                                        <span className="approval-key">
                                                                            {requestedEntitlementLabel}
                                                                        </span>
                                                                        {requestedRoleNames.length
                                                                            ? requestedRoleNames.join(', ')
                                                                            : requestedGroupName || 'Not provided'}
                                                                    </p>
                                                                    <p>
                                                                        <span className="approval-key">Job responsibilities</span>
                                                                        {jobResponsibilities || 'Not provided'}
                                                                    </p>
                                                                    <p>
                                                                        <span className="approval-key">Justification provided</span>
                                                                        {justification || 'Not provided'}
                                                                    </p>
                                                                </div>
                                                            </div>
                                                        </div>
                                                        {reviewSections.map(({ title, review }) => {
                                                            const hasDetailedReviewCard =
                                                                title === 'Supervisor Review' ||
                                                                title === 'Entitlement Owner Review' ||
                                                                title === 'Security Manager Review';
                                                            if (hasDetailedReviewCard) {
                                                                return null;
                                                            }
                                                            if (!review) {
                                                                return null;
                                                            }
                                                            const reviewDate = review.updatedAt
                                                                ? normalizeSubmittedAt(review.updatedAt)?.toLocaleString() ?? ''
                                                                : '';
                                                            return (
                                                                <div
                                                                    key={title}
                                                                    className="usa-summary-box approval-summary-box"
                                                                    role="region"
                                                                    aria-labelledby={`${title}-details`}
                                                                >
                                                                    <div className="usa-summary-box__body">
                                                                        <h4
                                                                            className="usa-summary-box__heading"
                                                                            id={`${title}-details`}
                                                                        >
                                                                            {title}
                                                                        </h4>
                                                                        <div className="usa-summary-box__text">
                                                                            <p>
                                                                                <span className="approval-key">Decision</span>
                                                                                {review.decision ?? 'Not provided'}
                                                                                {reviewDate ? ` · ${reviewDate}` : ''}
                                                                            </p>
                                                                            <p>
                                                                                <span className="approval-key">Notes</span>
                                                                                {review.notes || 'None provided.'}
                                                                            </p>
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                            );
                                                        })}
                                                    </div>
                                                    <div className="approval-summary-column">
                                                        <div
                                                            className="usa-summary-box approval-summary-box"
                                                            role="region"
                                                            aria-labelledby="requester-details"
                                                        >
                                                            <div className="usa-summary-box__body">
                                                                <h4
                                                                    className="usa-summary-box__heading"
                                                                    id="requester-details"
                                                                >
                                                                    Requester Details
                                                                </h4>
                                                                <div className="usa-summary-box__text">
                                                                    <div className="approval-requester">
                                                                        <div
                                                                            className={
                                                                                requesterPhotoUrl
                                                                                    ? 'approval-requester__avatar approval-requester__avatar--photo'
                                                                                    : 'approval-requester__avatar'
                                                                            }
                                                                            aria-hidden={!requesterPhotoUrl}
                                                                        >
                                                                            {requesterPhotoUrl ? (
                                                                                <img
                                                                                    className="approval-requester__avatar-image"
                                                                                    src={requesterPhotoUrl}
                                                                                    alt={`${requesterFirstName} ${requesterLastName}`.trim() || 'Requester'}
                                                                                />
                                                                            ) : null}
                                                                        </div>
                                                                        <div className="approval-requester__info">
                                                                            <p>
                                                                                <span className="approval-key">First name</span>
                                                                                {requesterFirstName || 'Not provided'}
                                                                            </p>
                                                                            <p>
                                                                                <span className="approval-key">Last name</span>
                                                                                {requesterLastName || 'Not provided'}
                                                                            </p>
                                                                            <p>
                                                                                <span className="approval-key">Username</span>
                                                                                {requesterUsername || 'Not provided'}
                                                                            </p>
                                                                            <p>
                                                                                <span className="approval-key">Email</span>
                                                                                <CopyableEmail
                                                                                    email={requesterEmail}
                                                                                    currentUserEmail={currentUserEmail}
                                                                                />
                                                                            </p>
                                                                            {requesterJobTitle ? (
                                                                                <p>
                                                                                    <span className="approval-key">Job title</span>
                                                                                    {requesterJobTitle}
                                                                                </p>
                                                                            ) : null}
                                                                            {requesterUserType ? (
                                                                                <p>
                                                                                    <span className="approval-key">User type</span>
                                                                                    {requesterUserType}
                                                                                </p>
                                                                            ) : null}
                                                                            {requesterContractNumber ? (
                                                                                <p>
                                                                                    <span className="approval-key">Contract number</span>
                                                                                    {requesterContractNumber}
                                                                                </p>
                                                                            ) : null}
                                                                            {requesterContractExpirationDate ? (
                                                                                <p>
                                                                                    <span className="approval-key">
                                                                                        Contract expiration date
                                                                                    </span>
                                                                                    {requesterContractExpirationDate}
                                                                                </p>
                                                                            ) : null}
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                    {currentStep === 2 || currentStep === 3 || currentStep === 4 ? (
                                                        <>
                                                            <div className="approval-summary-divider" />
                                                            <div className="approval-summary-column approval-summary-column--full">
                                                                <div
                                                                    className="usa-summary-box approval-summary-box"
                                                                    role="region"
                                                                    aria-labelledby="supervisor-approval-details"
                                                                >
                                                                    <div className="usa-summary-box__body">
                                                                        <h4
                                                                            className="usa-summary-box__heading approval-summary-heading"
                                                                            id="supervisor-approval-details"
                                                                        >
                                                                            Supervisor Review
                                                                            {currentStep === 2 ? (
                                                                                <span className="usa-tag usa-tag--warn">Pending approval</span>
                                                                            ) : (
                                                                                <span className="usa-tag">
                                                                                    {supervisorSummaryReview?.decision || 'Approved'}
                                                                                </span>
                                                                            )}
                                                                        </h4>
                                                                        <div className="usa-summary-box__text">
                                                                            <div className="approval-requester approval-requester--row">
                                                                                <div className="approval-requester__primary">
                                                                                    <div
                                                                                        className={
                                                                                            supervisorPhotoUrl
                                                                                                ? 'approval-requester__avatar approval-requester__avatar--photo approval-requester__avatar--supervisor'
                                                                                                : 'approval-requester__avatar approval-requester__avatar--supervisor'
                                                                                        }
                                                                                        aria-hidden={!supervisorPhotoUrl}
                                                                                    >
                                                                                        {supervisorPhotoUrl && !isLoadingSupervisor ? (
                                                                                            <img
                                                                                                className="approval-requester__avatar-image"
                                                                                                src={supervisorPhotoUrl}
                                                                                                alt={supervisorDisplayName || 'Supervisor'}
                                                                                            />
                                                                                        ) : null}
                                                                                    </div>
                                                                                    <div className="approval-requester__info">
                                                                                        <p>
                                                                                            <span className="approval-key">Name</span>
                                                                                            <ValueWithSkeleton
                                                                                                value={supervisorDisplayName}
                                                                                                loading={isLoadingSupervisor}
                                                                                                fallback="Supervisor"
                                                                                                width="12ch"
                                                                                            />
                                                                                        </p>
                                                                                        <p>
                                                                                            <span className="approval-key">Email</span>
                                                                                            <CopyableEmail
                                                                                                email={supervisorEmail}
                                                                                                currentUserEmail={currentUserEmail}
                                                                                            />
                                                                                        </p>
                                                                                        {currentStep >= 3 ? (
                                                                                            <p>
                                                                                                <span className="approval-key">Decision</span>
                                                                                                {supervisorSummaryReview?.decision || 'Not provided'}
                                                                                                {supervisorReviewDateDisplay
                                                                                                    ? ` · ${supervisorReviewDateDisplay}`
                                                                                                    : ''}
                                                                                            </p>
                                                                                        ) : null}
                                                                                        {currentStep >= 3 ? (
                                                                                            <p>
                                                                                                <span className="approval-key">Notes</span>
                                                                                                {supervisorSummaryReview?.notes || 'None provided.'}
                                                                                            </p>
                                                                                        ) : null}
                                                                                    </div>
                                                                                </div>
                                                                                <div className="approval-requester__outstanding">
                                                                                    <p className="approval-requester__outstanding-value">
                                                                                        {supervisorDurationDays ?? '—'}
                                                                                    </p>
                                                                                    <p className="approval-requester__outstanding-label">
                                                                                        {currentStep >= 3 ? 'Days to complete' : 'Days outstanding'}
                                                                                    </p>
                                                                                </div>
                                                                            </div>
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                                <div
                                                                    className="usa-summary-box approval-summary-box"
                                                                    role="region"
                                                                    aria-labelledby="entitlement-owner-review-details"
                                                                >
                                                                    <div className="usa-summary-box__body">
                                                                        <h4
                                                                            className="usa-summary-box__heading approval-summary-heading"
                                                                            id="entitlement-owner-review-details"
                                                                        >
                                                                            Entitlement Owner Review
                                                                            {currentStep === 2 ? (
                                                                                <span className="usa-tag usa-tag--info">Next</span>
                                                                            ) : currentStep === 3 ? (
                                                                                <span className="usa-tag usa-tag--warn">Pending</span>
                                                                            ) : (
                                                                                <span className="usa-tag">
                                                                                    {entitlementOwnerSummaryReview?.decision || 'Complete'}
                                                                                </span>
                                                                            )}
                                                                        </h4>
                                                                        <div className="usa-summary-box__text">
                                                                            <div
                                                                                className={
                                                                                    currentStep >= 3
                                                                                        ? 'approval-requester approval-requester--row approval-requester--row-top-aligned'
                                                                                        : 'approval-requester approval-requester--row'
                                                                                }
                                                                            >
                                                                                <div className="approval-requester__primary">
                                                                                    <div
                                                                                        className={
                                                                                            entitlementOwnerGroupPhotoUrl
                                                                                                ? 'approval-requester__avatar approval-requester__avatar--photo approval-requester__avatar--supervisor'
                                                                                                : 'approval-requester__avatar approval-requester__avatar--supervisor'
                                                                                        }
                                                                                        aria-hidden={!entitlementOwnerGroupPhotoUrl}
                                                                                    >
                                                                                        {entitlementOwnerGroupPhotoUrl ? (
                                                                                            <img
                                                                                                className="approval-requester__avatar-image"
                                                                                                src={entitlementOwnerGroupPhotoUrl}
                                                                                                alt={entitlementOwnerGroupName || 'Entitlement owner group'}
                                                                                            />
                                                                                        ) : null}
                                                                                    </div>
                                                                                    <div className="approval-requester__info">
                                                                                        {currentStep < 4 ? (
                                                                                            <p>
                                                                                                <span className="approval-key">Status</span>
                                                                                                {currentStep === 2
                                                                                                    ? 'Waiting on supervisor approval'
                                                                                                    : 'Pending approval'}
                                                                                            </p>
                                                                                        ) : null}
                                                                                        {currentStep >= 4 ? (
                                                                                            <p>
                                                                                                <span className="approval-key">Decision</span>
                                                                                                {entitlementOwnerSummaryReview?.decision || 'Not provided'}
                                                                                                {entitlementOwnerReviewDateDisplay
                                                                                                    ? ` · ${entitlementOwnerReviewDateDisplay}`
                                                                                                    : ''}
                                                                                            </p>
                                                                                        ) : null}
                                                                                        {currentStep >= 4 ? (
                                                                                            <p>
                                                                                                <span className="approval-key">Notes</span>
                                                                                                {entitlementOwnerSummaryReview?.notes || 'None provided.'}
                                                                                            </p>
                                                                                        ) : null}
                                                                                        {currentStep >= 4 ? (
                                                                                            <p>
                                                                                                <span className="approval-key">Reviewing group</span>
                                                                                                {entitlementOwnerGroupName}
                                                                                            </p>
                                                                                        ) : null}
                                                                                        {currentStep >= 4 ? (
                                                                                            <div className="approval-reviewer-subsection">
                                                                                                <p className="approval-reviewer-subsection__title">Reviewer details</p>
                                                                                                <div className="approval-reviewer-subsection__content">
                                                                                                    <div
                                                                                                        className={
                                                                                                            entitlementOwnerReviewerPhotoUrl
                                                                                                                ? 'approval-requester__avatar approval-requester__avatar--photo approval-requester__avatar--reviewer'
                                                                                                                : 'approval-requester__avatar approval-requester__avatar--reviewer'
                                                                                                        }
                                                                                                        aria-hidden={!entitlementOwnerReviewerPhotoUrl}
                                                                                                    >
                                                                                                        {entitlementOwnerReviewerPhotoUrl ? (
                                                                                                            <img
                                                                                                                className="approval-requester__avatar-image"
                                                                                                                src={entitlementOwnerReviewerPhotoUrl}
                                                                                                                alt={entitlementOwnerReviewerDisplayName || 'Entitlement owner reviewer'}
                                                                                                            />
                                                                                                        ) : null}
                                                                                                    </div>
                                                                                                    <div className="approval-reviewer-subsection__info">
                                                                                                        <p>
                                                                                                            <span className="approval-key">Reviewed by</span>
                                                                                                            <ValueWithSkeleton
                                                                                                                value={entitlementOwnerReviewerDisplayName}
                                                                                                                loading={isLoadingEntitlementOwnerReviewer}
                                                                                                                fallback="Not provided"
                                                                                                                width="13ch"
                                                                                                            />
                                                                                                        </p>
                                                                                                        <p>
                                                                                                            <span className="approval-key">Reviewer email</span>
                                                                                                            <CopyableEmail
                                                                                                                email={entitlementOwnerReviewerEmail}
                                                                                                                currentUserEmail={currentUserEmail}
                                                                                                            />
                                                                                                        </p>
                                                                                                    </div>
                                                                                                </div>
                                                                                            </div>
                                                                                        ) : null}
                                                                                        {currentStep < 4 ? (
                                                                                            <p>
                                                                                                <span className="approval-key">Reviewing group</span>
                                                                                                {entitlementOwnerGroupName}
                                                                                            </p>
                                                                                        ) : null}
                                                                                        {currentStep === 3 ? (
                                                                                            <div className="approval-reviewer-subsection">
                                                                                                <p className="approval-reviewer-subsection__title">Approvers pool</p>
                                                                                                {isLoadingEntitlementOwnerPotentialApprovers ? (
                                                                                                    <p className="approval-reviewer-subsection__helper">
                                                                                                        <ValueWithSkeleton loading type="sentence" fallback={null} />
                                                                                                    </p>
                                                                                                ) : entitlementOwnerPotentialApprovers.length ? (
                                                                                                    <div className="approval-reviewer-subsection__grid">
                                                                                                        {entitlementOwnerPotentialApprovers.map((approver) => {
                                                                                                            const approverPhotoUrl =
                                                                                                                entitlementOwnerPotentialApproverPhotoLookup.get(approver.id);
                                                                                                            return (
                                                                                                                <div
                                                                                                                    key={approver.id}
                                                                                                                    className="approval-reviewer-subsection__content approval-reviewer-subsection__content--grid-item"
                                                                                                                >
                                                                                                                    <div
                                                                                                                        className={
                                                                                                                            approverPhotoUrl
                                                                                                                                ? 'approval-requester__avatar approval-requester__avatar--photo approval-requester__avatar--reviewer'
                                                                                                                                : 'approval-requester__avatar approval-requester__avatar--reviewer'
                                                                                                                        }
                                                                                                                        aria-hidden={!approverPhotoUrl}
                                                                                                                    >
                                                                                                                        {approverPhotoUrl ? (
                                                                                                                            <img
                                                                                                                                className="approval-requester__avatar-image"
                                                                                                                                src={approverPhotoUrl}
                                                                                                                                alt={approver.displayName}
                                                                                                                            />
                                                                                                                        ) : null}
                                                                                                                    </div>
                                                                                                                    <div className="approval-reviewer-subsection__info">
                                                                                                                        <p>
                                                                                                                            <span className="approval-key">Approver</span>
                                                                                                                            {approver.displayName}
                                                                                                                        </p>
                                                                                                                        <p>
                                                                                                                            <span className="approval-key">Approver email</span>
                                                                                                                            <CopyableEmail
                                                                                                                                email={approver.email}
                                                                                                                                currentUserEmail={currentUserEmail}
                                                                                                                            />
                                                                                                                        </p>
                                                                                                                    </div>
                                                                                                                </div>
                                                                                                            );
                                                                                                        })}
                                                                                                    </div>
                                                                                                ) : (
                                                                                                    <p className="approval-reviewer-subsection__helper">
                                                                                                        No approvers listed.
                                                                                                    </p>
                                                                                                )}
                                                                                            </div>
                                                                                        ) : null}
                                                                                    </div>
                                                                                </div>
                                                                                <div className="approval-requester__outstanding">
                                                                                    <p className="approval-requester__outstanding-value">
                                                                                        {currentStep < 3 ? 'N/A' : entitlementOwnerDurationDays ?? '—'}
                                                                                    </p>
                                                                                    <p className="approval-requester__outstanding-label">
                                                                                        {currentStep >= 4 ? 'Days to complete' : 'Days outstanding'}
                                                                                    </p>
                                                                                </div>
                                                                            </div>
                                                                            {shouldShowEntitlementAccessGuide ? (
                                                                                <div className="approval-guide-subsection" aria-live="polite">
                                                                                    <p className="approval-guide-subsection__title">Access guide</p>
                                                                                    {isLoadingEntitlementAccessGuide ? (
                                                                                        <p className="approval-guide-subsection__text">
                                                                                            <ValueWithSkeleton
                                                                                                loading
                                                                                                fallback={null}
                                                                                                type="paragraph"
                                                                                            />
                                                                                        </p>
                                                                                    ) : (
                                                                                        <div
                                                                                            className="approval-guide-subsection__text"
                                                                                            dangerouslySetInnerHTML={{
                                                                                                __html: renderMarkdownToSafeHtml(entitlementAccessGuide),
                                                                                            }}
                                                                                        />
                                                                                    )}
                                                                                </div>
                                                                            ) : null}
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        </>
                                                    ) : null}
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                    <div className="usa-card__footer">
                                        <Link to="/requests" className="usa-button usa-button--outline">
                                            Back to requests
                                        </Link>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>
        </AppLayout>
    );
};

export default RequestDetailPage;
